/* import __COLOCATED_TEMPLATE__ from './svg-spur-graph.hbs'; */
import Component from '@glimmer/component';
import Owner from '@ember/owner';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { format } from 'd3-format';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import type IntlService from 'ember-intl/services/intl';
import Zoom, { ZoomRect, ZoomPosition, ZoomLimits } from '../models/graphs/zoom';
import type { SpurGraphLimits } from '../models/graphs/spur-graph';

interface SvgSpurGraphArgs {
  dataPoints: number[][][];
  limitPoints: SpurGraphLimits;
  mask: boolean[];
  suppress: boolean;
}

interface SvgSpurGraphSignature {
  Args: SvgSpurGraphArgs;
  Element: SVGElement;
}

interface HoverContent {
  x: number;
  y: number;
  path: string;
  width: number;
  height: number;
  text: string;
}
const leftLabelOffset = 55,
  bottomLabelOffset = 60,
  hoverBox = [110, 25],
  classes = ['image', 'second', 'third', 'ppt', 'fundamental', 'filter'];

/**
 * A field for display of frequency data
 *
 * @extends {Component<SvgSpurGraphSignature>}
 */
export default class SvgSpurGraph extends Component<SvgSpurGraphSignature> {
  @service declare intl: IntlService;
  zoom = new Zoom();
  leftMargin = 70;
  topMargin = 10;
  rightMargin = 5;
  bottomMargin = 65;
  xTitle = '';
  yTitle = '';
  svgElement: SVGElement | null = null;

  // Provisional dimensions for initial layout, just to ensure everything is there
  // Real dimensions come from CSS after we have a tag to query
  @tracked width = 620;
  @tracked height = 290;

  @tracked hover: HoverContent | null = null;
  @tracked hoverText = '';
  @tracked limits: SpurGraphLimits;
  @tracked graphMask: boolean[] = [];
  @tracked showReturnTo100 = false;
  @tracked isSelectionActive?: ZoomPosition;
  @tracked zoomRectangle?: ZoomRect;

  constructor(owner: Owner, args: SvgSpurGraphArgs) {
    super(owner, args);
    this.xTitle = this.intl.t('spur-graph.frequency_axis_text');
    this.yTitle = this.intl.t('spur-graph.amplitude_axis_text');
    this.hover = null;
    this.limits = Object.assign({}, this.args.limitPoints);
    this.argsUpdated();
  }

  @action argsUpdated() {
    let refreshGraph = true;
    if (this.graphMask !== null) {
      if (
        this.graphMask[0] !== this.args.mask[0] ||
        this.graphMask[1] !== this.args.mask[1] ||
        this.graphMask[2] !== this.args.mask[2] ||
        this.graphMask[3] !== this.args.mask[3] ||
        this.graphMask[4] !== this.args.mask[4]
      ) {
        refreshGraph = false;
      }
    }
    // Defer update
    Promise.resolve().then(() => {
      this.graphMask = this.args.mask;
    });
    if (refreshGraph) {
      this.returnTo100();
    }
  }
  @action listenMouseUp(/*event: MouseEvent*/) {
    if (this.isSelectionActive) {
      this.handleMouseUp();
    }
  }
  @action inserted(element: SVGElement) {
    this.svgElement = element;
    window.addEventListener('mouseup', this.listenMouseUp);
    const rect = element.getBoundingClientRect();
    if (rect.width !== 620) {
      this.width = element.clientWidth; //rect.width;
    }
    if (rect.height !== 290) {
      this.height = element.clientHeight; //rect.height;
    }
    this.argsUpdated();
  }
  @action handleMouseOver(event: Event) {
    if (event.target && event.target instanceof SVGElement) {
      if (event.target.classList.contains('bar')) {
        const boxWidth = hoverBox[0] as number;
        const boxHeight = hoverBox[1] as number;
        const dataX = event.target.getAttribute('data-x');
        const dataY = event.target.getAttribute('data-y');
        const hz = format('.4s')(Number(dataX)) + 'Hz';
        const dB = format('.0f')(Number(dataY)) + 'dB';
        const x = parseFloat(event.target.getAttribute('x') ?? '');
        const y = parseFloat(event.target.getAttribute('y') ?? '');
        const boxX = Math.max(0, x - boxWidth / 2);
        const path = 'm' + (x - boxX - 5) + ',' + boxHeight + ' l10,0 l-5,5 z';
        this.hover = {
          x: boxX,
          y: Math.max(0, y - boxHeight),
          path: path,
          width: boxWidth,
          height: boxHeight as number,
          text: dB + '@' + hz,
        };
      }
    }
  }
  @action handleMouseOut(event: MouseEvent) {
    if (event.target && event.target instanceof SVGElement && event.target.tagName === 'svg') {
      this.hover = null;
    }
  }
  @action handleMouseDown(event: MouseEvent) {
    if (event.which === 1 || event.which === 2 || event.which === 3) {
      if (!this.args.suppress) {
        const elem = this.svgElement ? this.svgElement.querySelector('.frame') : undefined;
        if (elem && this.limits) {
          const zoomLimits: ZoomLimits = {
            minX: this.limits.minfreq,
            maxX: this.limits.maxfreq,
            minY: this.limits.mindb,
            maxY: this.limits.maxdb,
          };
          this.zoomRectangle = undefined;
          this.isSelectionActive = this.zoom.startZoomSelect(event, elem, this.xTicks, this.yTicks, zoomLimits);
        }
      }
      this.hover = null;
    }
  }
  @action handleMouseMove(event: MouseEvent) {
    if (!this.args.suppress && this.isSelectionActive) {
      this.zoomRectangle = this.zoom.moveZoomSelect(event);
    }
  }
  @action handleMouseUp() {
    if (!this.args.suppress) {
      const zoomLimits = this.zoom.stopZoomSelect(15, 15);
      if (zoomLimits) {
        this.limits = {
          minfreq: zoomLimits.minX,
          maxfreq: zoomLimits.maxX,
          intervalfreq: this.limits ? this.limits.intervalfreq : 1,
          mindb: zoomLimits.minY,
          maxdb: zoomLimits.maxY,
          intervaldb: this.limits ? this.limits.intervaldb : 2,
        };
        this.showReturnTo100 = true;
      }
    }
    this.zoomRectangle = undefined;
    this.isSelectionActive = undefined;
  }
  returnTo100() {
    this.zoomRectangle = undefined;
    this.limits = this.args.limitPoints;
    this.showReturnTo100 = false;
  }

  get data() {
    if (this.args.suppress) {
      return [];
    }
    return this.args.dataPoints;
  }

  get chartwidth() {
    return this.width - (this.leftMargin + this.rightMargin);
  }
  get chartheight() {
    return this.height - (this.topMargin + this.bottomMargin);
  }
  get xTitleCenter() {
    return { x: this.chartwidth / 2, y: this.chartheight + bottomLabelOffset };
  }
  get yTitleCenter() {
    return { x: -leftLabelOffset, y: this.chartheight / 2 };
  }
  get xTicks() {
    const xScale = scaleLinear().domain([this.limits.minfreq, this.limits.maxfreq]).range([0, this.chartwidth]);
    const xFormatter = format('.4s');
    return xScale.ticks(8).map((tick) => ({
      value: xFormatter(tick),
      x: xScale(tick),
      y: this.chartheight,
    }));
  }
  get yTicks() {
    const yScale = scaleLinear().domain([this.limits.mindb, this.limits.maxdb]).range([this.chartheight, 0]);
    const yFormatter = format('.0f');
    return yScale.ticks(12).map((tick) => ({ value: yFormatter(tick), x: 0, y: yScale(tick) }));
  }
  get spurs() {
    const xScale = scaleLinear().domain([this.limits.minfreq, this.limits.maxfreq]).range([0, this.chartwidth]);
    const yScale = scaleLinear().domain([this.limits.mindb, this.limits.maxdb]).range([this.chartheight, 0]);
    const spurlist = [];
    for (let seriesIndex = 0, seriesLen = this.data.length - 1; seriesIndex < seriesLen; seriesIndex++) {
      const series = this.data[seriesIndex] as number[][];
      const cssclass = classes[seriesIndex] as string;
      if (this.args.mask[seriesIndex]) {
        for (let spurIndex = 0, spursLen = series.length; spurIndex < spursLen; spurIndex++) {
          const point = series[spurIndex] as number[];
          const xValue = xScale(point[0] as number);
          const yValue = yScale(point[1] as number);
          if (xValue > 0 && xValue < this.chartwidth) {
            let height: number | null = this.chartheight - yValue;
            if (height < 0) {
              height = null;
            }

            spurlist.push({
              cls: cssclass,
              x: xValue,
              y: yValue,
              w: 2,
              h: height,
              rx: point[0] as number,
              ry: point[1] as number,
            });
          }
        }
      }
    }
    return spurlist;
  }
  get path() {
    if (this.args.suppress) {
      return null;
    }
    const plotdata = this.data[5] as number[][];
    const cleanedData: number[][] = [];
    for (let i = 0; i < plotdata.length; i++) {
      const point = plotdata[i] as number[];
      if ((point[1] as number) >= this.limits.mindb && (point[1] as number) <= this.limits.maxdb) {
        cleanedData.push(point);
      }
    }
    const xScale = scaleLinear().domain([this.limits.minfreq, this.limits.maxfreq]).range([0, this.chartwidth]);
    const yScale = scaleLinear().domain([this.limits.mindb, this.limits.maxdb]).range([this.chartheight, 0]);
    const result = line()
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]))(cleanedData as [number, number][]);
    return result;
  }
  get showHover() {
    return this.hover && !this.isSelectionActive;
  }
  @action resetZoom() {
    this.returnTo100();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    SvgSpurGraph: typeof SvgSpurGraph;
  }
}
